import { useEffect, useState } from 'react';
import { GridiconsSearch } from '../../../../../assets/icon/iconify';
import { useDispatch } from 'react-redux';
import { Spinner } from '../../../../../assets/icon/svg';
import { clearTypeModal } from '../../../../../features/gms-state-and-modal/modal-slice';
import { useFilterIndustries } from '../../../../../hooks/filter/use-filter-industries';
import { resetNatureOfBusiness, setNatureOfBusiness } from '../../../../../features/gms-proposal-forms/basic-information/basic-information.slice';
import { GroupedIndustriesResponse } from '../../../../../types/type';
import { useProposalTypeData } from '../../../../../hooks/proposal/use-proposal-type-data';

interface Props {
    setSearchValue: (value: string) => void;
    searchValue: string;
    industries: GroupedIndustriesResponse | undefined;
    setView: (value: 'category' | 'item') => void;
}

export default function IndustriesItems({ setView, setSearchValue, searchValue, industries }: Props) {
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const { natureOfBusinessCategory, natureOfBusinessItem  } = useProposalTypeData()

    const formatCategory = natureOfBusinessCategory?.name?.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

    useEffect(() => {
        if (!searchValue.trim()) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchValue]);

    const handleBackToCategory = () => {
        setView('category');
        dispatch(resetNatureOfBusiness({ type: 'item' }));
    };

    const industriesData = useFilterIndustries({
        industries: industries?.[natureOfBusinessCategory?.name ?? ''],
        searchValue,
    });

    return (
        <div className="space-y-2 col flex flex-col h-full">
            <div className="flex flex-col space-y-3 w-full">
                <div>
                    <button
                        onClick={handleBackToCategory}
                        className="text-xs text-blue-600 flex items-center"
                    >
                        ← Back
                    </button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                    <div className="text-sm font-medium col-span-2 flex items-center justify-start">
                        {formatCategory}
                    </div>

                    <div className="col-span-1">
                        <div className="relative w-full flex">
                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                                <GridiconsSearch height={18} width={18} />
                            </div>
                            <input
                                className={`border w-full text-xs rounded px-2 py-1 focus:outline-none pl-9 pr-3 ${searchValue.length > 1 ? 'border-[#186cb2] bg-[#186cb2]/10' : 'border-gray-300'}`}
                                type="text"
                                placeholder="Search by industries"
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <hr className="h-px bg-gray-200 border-0" />

                <div className={`grid ${isLoading ? 'grid-cols-1' : 'grid-cols-2'} gap-1.5 overflow-y-auto max-h-[60vh] pr-1`}>
                    {isLoading ? (
                        <div className="min-h-[300px] flex justify-center items-center">
                            <div className="flex justify-center items-center h-full w-full">
                                <Spinner color="#186cb2" size={30} />
                                <span className="ml-[5px]">Loading...</span>
                            </div>
                        </div>
                    ) : industriesData.length > 0 ? (
                        industriesData.map((item) => (
                            <label
                                key={item.id}
                                className={`gap-2 flex px-3 py-2 rounded border transition-all duration-200 items-center cursor-pointer bg-white capitalize ${natureOfBusinessItem?.id === item.id ? 'border-[#186cb2] bg-blue-50' : 'border-gray-300 hover:border-[#186cb2] hover:bg-gray-100'}`}
                            >
                                <input
                                    type="checkbox"
                                    onChange={() => {
                                        dispatch(setNatureOfBusiness({ type: 'item', value: { id: item.id, name: item.name } }));
                                        dispatch(clearTypeModal());
                                    }}
                                    className="hidden"
                                />

                                <span className="text-xs text-black flex-1 whitespace-normal break-all">
                                    {item.name}
                                </span>
                            </label>
                        ))
                    ) : (
                        <div className="col-span-2 text-center text-xs text-gray-500 py-5">
                            No industries found.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}